import { useMemo, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { Tab } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab';
import { TabList } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-list';
import { TabPanel } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-panel';

import { METER_CONTENT } from '../content/meter-content';
import { METER_ALLOWED_APPS } from '../context/constants';
import { useSelector, useToggleProduct } from '../context/hooks';
import {
	selectCanAdvanceAllowanceStep,
	selectIsProductSelected,
	selectMeter,
} from '../context/selectors';
import {
	useUbpStep4BackClickedEvent,
	useUbpStep4CompareClickedEvent,
	useUbpStep4DeselectButtonClickedEvent,
	useUbpStep4ScreenViewedEvent,
	useUbpStep4SelectButtonClickedEvent,
	useUbpStep4TabClickedEvent,
} from '../ubp-calculator-analytics';

import { messages } from './allowance-step.messages';
import { ChoiceCard } from './choice-card';
import { useAppDescriptions, useCollectionDescriptions } from './product-descriptions';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';

import type { BulletOrIconListItem } from './strings';
import type { ProductKey } from '../calculator/types';
import type { SelectableCollectionTypes, SelectableAppTypes } from '../context/types';

export const AllowanceStep = () => {
	// Manual tab-state management (instead of using the higher-level
	// `TabsSection`) so we can insert the design-required "Selections apply
	// to one tab at a time…" advisory copy between the tab-button row and
	// the tab-panel content. `TabsSection` doesn't expose a slot for
	// between-tabs-and-panel content, so we compose the primitives
	// (`TabList`, `Tab`, `TabPanel`) directly. Default active tab is
	// `collections`, satisfying the GWP-12102 requirement that clicking
	// Next on Estimate always lands on the Collections tab.
	const [activeTabId, setActiveTabId] = useState<'collections' | 'apps'>('collections');
	const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
	const meter = useSelector(selectMeter);
	const canAdvance = useSelector(selectCanAdvanceAllowanceStep);
	const intl = useIntl();

	const fireStep4Viewed = useUbpStep4ScreenViewedEvent();
	const fireStep4Tab = useUbpStep4TabClickedEvent();
	const fireStep4Compare = useUbpStep4CompareClickedEvent();
	const fireStep4Back = useUbpStep4BackClickedEvent();

	useEffect(() => {
		fireStep4Viewed();
		// Intentionally run once on mount only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const APP_STRINGS = useAppDescriptions();
	const COLLECTION_STRINGS = useCollectionDescriptions();

	// Filter the full app list down to only the apps allowed for the current
	// meter. METER_ALLOWED_APPS[meter] is null for csResolutions, but the
	// allowance step is never rendered for that meter (it is skipped in
	// navigation), so we fall back to all keys defensively.
	const allowedApps = METER_ALLOWED_APPS[meter];
	const appKeys = useMemo(
		() =>
			(Object.keys(APP_STRINGS) as SelectableAppTypes[]).filter(
				(key) => allowedApps === null || allowedApps.includes(key),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[APP_STRINGS, meter],
	);
	const collectionKeys = useMemo(
		() => Object.keys(COLLECTION_STRINGS) as SelectableCollectionTypes[],
		[COLLECTION_STRINGS],
	);

	return (
		<>
			<Stepper />
			{/*
			 * Allowance step H2 title per Figma node 1103:32366:
			 *   "Explore how Atlassian collections and apps cover your usage needs"
			 * Charlie Display Bold 40/48, centered, primary-black.
			 * The old "Allowance" placeholder heading was removed.
			 */}
			<h2 css={stepTitleStyle}>{intl.formatMessage(messages.pageTitle)}</h2>
			{/*
			 * WAC design-system tab primitives composed directly (instead of
			 * `TabsSection`) so the design-required advisory copy can sit
			 * between the tab-button row and the tab-panel content. Order:
			 *   1. TabList — round tab buttons "Atlassian Collections" / "Atlassian apps"
			 *   2. Advisory copy — body-small, muted, centered, 40px above and 40px below
			 *   3. TabPanel — the currently active tab's card content
			 */}
			<div css={tabListWrapperStyle}>
				<TabList activeTabId={activeTabId}>
					<Tab
						tabId="collections"
						index={0}
						title={intl.formatMessage(messages.tabCollections)}
						isActive={activeTabId === 'collections'}
						onTabSelect={(id) => {
							setActiveTabId(id as 'collections' | 'apps');
							fireStep4Tab('collections');
						}}
						tabSize="small"
					/>
					<Tab
						tabId="apps"
						index={1}
						title={intl.formatMessage(messages.tabApps)}
						isActive={activeTabId === 'apps'}
						onTabSelect={(id) => {
							setActiveTabId(id as 'collections' | 'apps');
							fireStep4Tab('apps');
						}}
						tabSize="small"
					/>
				</TabList>
			</div>
			<p css={tabsAdvisoryStyle}>
				<span css={tabsAdvisorySpan}>
					{intl.formatMessage({
						id: 'ubp.calculator.allowance.tabsadvisory',
						defaultMessage: 'Switching tabs and selecting new items will clear previous choices.',
					})}
				</span>
			</p>
			{/*
			 * Grid wrapper so both TabPanels stack in the same cell
			 * (their `gridArea: '1 / 1'` requires a grid parent).
			 * Without this, the inactive panel still occupies its own row
			 * and pushes the visible panel down.
			 */}
			<div css={tabPanelStackStyle}>
				<TabPanel id="collections" isActive={activeTabId === 'collections'}>
					<div css={[allProductsGridStyle, collectionsGrid]}>
						{collectionKeys.map((choice: SelectableCollectionTypes) => {
							const { lockupUrl, description, list } = COLLECTION_STRINGS[choice];
							return (
								<CollectionCard
									key={`collections-${choice}`}
									productKey={choice}
									lockupUrl={lockupUrl}
									description={description}
									list={list}
								/>
							);
						})}
					</div>
				</TabPanel>
				<TabPanel id="apps" isActive={activeTabId === 'apps'}>
					<div css={[allProductsGridStyle, appsGrid]}>
						{appKeys.map((choice: SelectableAppTypes) => {
							const { label, iconUrl, description } = APP_STRINGS[choice];
							return (
								<AppCard
									key={`apps-${choice}`}
									productKey={choice}
									label={label}
									iconUrl={iconUrl}
									description={description}
								/>
							);
						})}
					</div>
				</TabPanel>
			</div>
			{/*
			 * Back + Next only — no Skip per GWP-12102 comment thread. Mirrors
			 * the Estimate step's action-row pattern.
			 */}

			<div css={errorProceed}>
				{hasAttemptedNext &&
					!canAdvance &&
					intl.formatMessage({
						id: 'ubp.calculator.allowance.error',
						defaultMessage: 'Select at least one collection or app to continue.',
					})}
			</div>
			{/*
			 * Meter-aware Next label — sourced from the atomic content
			 * registry (`allowanceStep.nextCta` in each meter file). Some
			 * meters prefer "Compare" (e.g. Automations) instead of "Next".
			 */}
			<StepNav
				showBack
				showNext
				nextLabel={intl.formatMessage(METER_CONTENT[meter].allowanceStep.nextCta)}
				onBeforeNext={() => {
					setHasAttemptedNext(true);
					if (!canAdvance) return false;
					fireStep4Compare();
				}}
				onBeforeBack={fireStep4Back}
			/>
		</>
	);
};

interface AppCardProps {
	productKey: ProductKey;
	label: string;
	iconUrl: string;
	description: string;
	hide?: boolean;
}

const AppCard = ({ productKey, label, iconUrl, description, hide = false }: AppCardProps) => {
	const isSelected = useSelector(selectIsProductSelected(productKey));
	const toggle = useToggleProduct();
	const fireSelect = useUbpStep4SelectButtonClickedEvent();
	const fireDeselect = useUbpStep4DeselectButtonClickedEvent();
	return (
		<ChoiceCard
			inputType="checkbox"
			value={productKey}
			selected={isSelected}
			onSelect={() => {
				toggle(productKey, !isSelected);
				if (isSelected) {
					fireDeselect(productKey);
				} else {
					fireSelect(productKey);
				}
			}}
			groupName="group-apps"
			label={label}
			iconUrl={iconUrl}
			description={description}
			hide={hide}
		/>
	);
};

interface CollectionCardProps {
	productKey: ProductKey;
	lockupUrl: string;
	description: string | ReactNode;
	list: BulletOrIconListItem[];
}

const CollectionCard = ({ productKey, lockupUrl, description, list }: CollectionCardProps) => {
	const isSelected = useSelector(selectIsProductSelected(productKey));
	const toggle = useToggleProduct();
	const fireSelect = useUbpStep4SelectButtonClickedEvent();
	const fireDeselect = useUbpStep4DeselectButtonClickedEvent();
	return (
		<ChoiceCard
			inputType="checkbox"
			value={productKey}
			selected={isSelected}
			onSelect={() => {
				toggle(productKey, !isSelected);
				if (isSelected) {
					fireDeselect(productKey);
				} else {
					fireSelect(productKey);
				}
			}}
			groupName="group-collections"
			lockupUrl={lockupUrl}
			description={description}
			list={list}
		/>
	);
};

// H2 title per Figma node 1103:32370 (Desktop / D-H2 style):
// Charlie Display Bold 40/48, primary-black, center-aligned. 80px top
// padding (40 base + 40 extra requested for this step) gives extra
// breathing room between the Stepper and the title; 40px below keeps
// the bottom rhythm consistent with the other steps.
const stepTitleStyle = css({
	margin: 0,
	paddingBlockStart: '80px',
	paddingBlockEnd: '40px',
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '40px',
	lineHeight: '48px',
	fontWeight: 700,
	textAlign: 'center',
	color: wacToken('color.neutral.light1100'),
});

// Wrapper for the raw <TabList>. The TabList's outermost element is
// `width: fit-content`, so to center it we flex-center this wrapper.
// This is safe for the sliding highlight pill: the pill measures the
// active tab's offset relative to the (fit-content) <ul>, and flex
// centering only repositions that fit-content block — it doesn't change
// the <ul>'s internal width, so `useScrollableTabList`'s measurement
// stays correct. On mobile we switch to flex-start to left-align.
const tabListWrapperStyle = css({
	display: 'flex',
	justifyContent: 'center',
	maxWidth: '100%',
	// The design-library <TabList> deliberately adds a 32px
	// `padding-inline-start` to the FIRST <li> (and 32px trailing on the
	// LAST) on mobile (see tab-list.tsx `WCL_GRID_PADDING_SM`). That gutter
	// is meant for full-bleed native tab rows, but our calculator container
	// already supplies its own 32px gutter — so the two stack and the first
	// tab ends up ~64px from the viewport edge. Zero the leading pad on
	// mobile so the first tab sits flush with the container's left edge.
	// Scoped under the same `below.sm` breakpoint the library uses so this
	// override wins by equal specificity + later source order.
	[media.below.sm]: {
		justifyContent: 'flex-start',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& ul > li:first-of-type': {
			paddingInlineStart: 0,
		},
		// Mirror of the leading gutter: the library also adds 32px
		// `padding-inline-end` to the LAST <li> on mobile. Zero it so the
		// grey container hugs the last tab instead of showing an extra 32px
		// of trailing space inside the pill.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& ul > li:last-of-type': {
			paddingInlineEnd: 0,
		},
		// The design-library <TabList> hides the sliding black highlight
		// pill below `sm` (highlightStyles: `[media.below.sm]: display none`)
		// because its native mobile pattern relies on tab text-color for the
		// active state. Our calculator design (Figma 2026-07-17) DOES want the
		// black pill on mobile, so re-enable it. The pill's left/width are set
		// via inline styles by `useScrollableTabList` at all viewports, so the
		// geometry is already correct — we only need to un-hide it. It's the
		// first child <div aria-hidden> inside the <ul>.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& ul > div[aria-hidden="true"]': {
			display: 'block',
		},
		// The design-library <TabList> makes the grey group-container
		// background transparent below `sm` (tabListContainerStyles:
		// `[media.below.sm]: { background: 'transparent' }`) because its
		// native mobile pattern is a bare scrollable row. Our calculator
		// design (Figma 2026-07-17) keeps the grey rounded pill container on
		// mobile too, so restore it. Target the TabList's root container —
		// the direct child <div> of this wrapper.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			background: wacToken('color.neutral.light200'),
		},
	},
});

// Grid wrapper for the tab panels. `TabPanel` positions itself at
// `gridArea: 1 / 1`, so all panels stack in a single grid cell and only
// the active one is visible (via opacity). Without this grid parent,
// panels flow one below the other and the "inactive" panel still takes
// vertical space, pushing the visible panel down.
const tabPanelStackStyle = css({
	display: 'grid',
});

// Body-small advisory copy that sits BETWEEN the tab-button row and the
// tab-panel content. 40px above/below per design.
//
// Mobile (< 768px): left-aligned so it flows with the left-aligned tab
// row above; tablet/desktop keeps the centered treatment matching the
// centered tabs.
const tabsAdvisoryStyle = css({
	margin: 0,
	paddingBlock: '40px',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '20px',
	textAlign: 'left',
	[media.above.md]: {
		textAlign: 'center',
	},
});

const tabsAdvisorySpan = css({
	color: wacToken('color.primary.black'),
});

const collectionsGrid = css({
	gridTemplateColumns: '1fr 1fr',
	[media.below.sm]: {
		gridTemplateColumns: '1fr',
	},
});

const appsGrid = css({
	gridTemplateColumns: '1fr 1fr 1fr',
	[media.only.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
	[media.below.sm]: {
		gridTemplateColumns: '1fr',
	},
});

const allProductsGridStyle = css({
	display: 'grid',
	gap: token('space.400'),
	[media.below.sm]: {
		gap: token('space.300'),
	},
});

const errorProceed = css({
	color: wacToken('color.secondary.red'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	textAlign: 'center',
	minHeight: '3rem',
});
